'use client';

import Script from 'next/script';
import React, { Fragment } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { CompanyPageImages, CompanyVideo } from '../markupConfig';

import { AppImageClient, AppImageData } from '@/shared/components/appImage';
import { LazyVideo } from '@/shared/components/lazyVideo';
import { useIsMobile } from '@/shared/hooks';

interface CompanyTabContentProps {
  pageTranslations: Record<string, string>;
  images: CompanyPageImages;
  beautifulMapsVideo: CompanyVideo;
}

export const CompanyTabContent: React.FC<CompanyTabContentProps> = ({
  pageTranslations,
  images,
  beautifulMapsVideo,
}) => {
  const isMobile = useIsMobile();
  const contentList = [
    {
      id: 'detalizationMapSectionFeaturesDetailedMap',
      content: (
        <DetailedMapBlock
          title={
            pageTranslations.detalizationMapSectionFeaturesDetailedMapTitle
          }
          description={
            pageTranslations.detalizationMapSectionFeaturesDetailedMapDescription
          }
          video={beautifulMapsVideo}
        />
      ),
    },
    {
      id: 'detalizationMapSectionFeaturesDetailsRoadObjectsDescription',
      content: (
        <Card
          id="detalizationMapSectionFeaturesDetailsRoadObjectsDescription"
          description={
            pageTranslations.detalizationMapSectionFeaturesDetailsRoadObjectsDescription
          }
          image={images.companyTab1}
        />
      ),
    },
    {
      id: 'detalizationMapSectionFeaturesFloorPlansDescription',
      content: (
        <Card
          id="detalizationMapSectionFeaturesFloorPlansDescription"
          description={
            pageTranslations.detalizationMapSectionFeaturesFloorPlansDescription
          }
          image={images.companyTab2}
        />
      ),
    },
    {
      id: 'detalizationMapSectionFeaturesPoiIconsDescription',
      content: (
        <Card
          id="detalizationMapSectionFeaturesPoiIconsDescription"
          description={
            pageTranslations.detalizationMapSectionFeaturesPoiIconsDescription
          }
          image={images.companyTab3}
        />
      ),
    },
    {
      id: 'detalizationMapSectionFeatures3dModelsDescription',
      content: (
        <Card
          id="detalizationMapSectionFeatures3dModelsDescription"
          description={
            pageTranslations.detalizationMapSectionFeatures3dModelsDescription
          }
          image={images.companyTab4}
        />
      ),
    },
    {
      id: 'detalizationMapSectionFeaturesImmersiveObjectsDescription',
      content: (
        <ImmersiveObjectsBlock
          description={
            pageTranslations.detalizationMapSectionFeaturesImmersiveObjectsDescription
          }
          images={images}
        />
      ),
    },
  ];
  if (isMobile) {
    return (
      <Swiper spaceBetween={12} slidesPerView="auto" className="!px-5 !py-2">
        {contentList.map((content) => (
          <SwiperSlide key={content.id}>{content.content}</SwiperSlide>
        ))}
      </Swiper>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-[32px] max-lg:flex-col">
      {contentList.map((content) => (
        <Fragment key={content.id}>{content.content}</Fragment>
      ))}
    </div>
  );
};

const ModelViewer = React.memo(({ images }: { images: CompanyPageImages }) => {
  return (
    <>
      <div className="rounded-20 relative h-[342px] w-[560px] object-cover max-lg:h-[246px] max-lg:w-full">
        <AppImageClient
          {...images['3dControls']}
          alt="model"
          className="absolute bottom-[20px] left-[23px] z-[2] h-[24px] w-auto"
        />
        {/* @ts-ignore */}
        <model-viewer
          camera-controls=""
          shadow-intensity="1"
          src={'/models/sakurapark_all.glb'}
          touch-action="pan-x"
          ar-status="not-presenting"
          className="rounded-20 h-full w-full overflow-hidden bg-white"
          //@ts-ignore
        ></model-viewer>
      </div>

      <Script
        src="https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js"
        type="module"
      />
    </>
  );
});
const DetailedMapBlock = React.memo(
  ({
    title,
    description,
    video,
  }: {
    title: string;
    description: string;
    video: CompanyVideo;
  }) => (
    <div className="max-lg:bg-surface-base-secondary relative col-span-2 h-[624px] w-full max-lg:h-full max-lg:w-full max-lg:rounded-[20px] max-lg:p-1">
      <LazyVideo
        src={video.src}
        poster={video.poster}
        className="h-full w-full rounded-[16px] object-cover max-lg:h-auto max-lg:w-full"
      />
      <div className="rounded-20 bg-surface-base-secondary absolute top-[8px] left-[8px] w-[568px] px-4 py-[25px] max-lg:static max-lg:w-full max-lg:bg-transparent max-lg:px-4 max-lg:py-4">
        <p
          id="detalizationMapSectionFeaturesDetailedMapTitle"
          className="text-text-base-primary text-[24px] leading-[32px] font-medium max-lg:text-[18px] max-lg:leading-[24px]"
        >
          {title}
        </p>
        <p
          id="detalizationMapSectionFeaturesDetailedMapDescription"
          className="text-text-base-primary mt-2 max-w-[450px] text-[16px] leading-[24px] max-lg:text-[14px] max-lg:leading-[20px]"
        >
          {description}
        </p>
      </div>
    </div>
  ),
);

const ImmersiveObjectsBlock = React.memo(
  ({
    description,
    images,
  }: {
    description: string;
    images: CompanyPageImages;
  }) => (
    <div className="rounded-20 bg-surface-base-secondary col-span-2 flex w-full justify-between p-2 max-lg:h-full max-lg:flex-col">
      <ModelViewer images={images} />
      <div className="ml-8 flex-1 px-4 py-10 max-lg:ml-0 max-lg:px-2 max-lg:py-4">
        <p
          id="detalizationMapSectionFeaturesImmersiveObjectsDescription"
          className="text-text-base-primary mt-3 max-w-[507px] text-[16px] leading-[24px] max-lg:text-[14px] max-lg:leading-[20px]"
        >
          {description}
        </p>
      </div>
    </div>
  ),
);

const Card = ({
  id,
  description,
  image,
}: {
  id: string;
  description: string;
  image: AppImageData;
}) => {
  return (
    <div className="rounded-20 bg-surface-base-secondary h-full w-full p-2">
      <AppImageClient
        {...image}
        alt="Card"
        className="h-[300px] w-[560px] rounded-[16px] object-cover max-lg:h-auto max-lg:w-full"
      />

      <div className="w-full px-3 py-6">
        <p
          id={id}
          className="text-text-base-primary max-w-[507px] text-[16px] leading-[24px] max-lg:text-[14px] max-lg:leading-[20px]"
        >
          {description}
        </p>
      </div>
    </div>
  );
};
